import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Breadcrumb,
  Tag,
  Badge,
  Checkbox,
  message,
  Divider,
  Typography,
  Select,
} from 'antd';
import {
  SearchOutlined,
  TrophyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import UserDetail from './UserDetail';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { Helmet } from 'react-helmet';

const { Title, Text } = Typography;

// ランク表示名マッピング
const rankDisplayNames = {
  white_1: 'ホワイト I',
  white_2: 'ホワイト II',
  white_3: 'ホワイト III',
  white_4: 'ホワイト IV',
  white_5: 'ホワイト V',
  bronze_1: 'ブロンズ I',
  bronze_2: 'ブロンズ II',
  bronze_3: 'ブロンズ III',
  silver_1: 'シルバー I',
  silver_2: 'シルバー II',
  silver_3: 'シルバー III',
  gold_1: 'ゴールド I',
  gold_2: 'ゴールド II',
  gold_3: 'ゴールド III',
  platinum_1: 'プラチナ I',
  platinum_2: 'プラチナ II',
  platinum_3: 'プラチナ III',
  diamond_1: 'ダイヤモンド I',
  diamond_2: 'ダイヤモンド II',
  diamond_3: 'ダイヤモンド III',
  legend: 'レジェンド',
};

// ランク色マッピング
const rankColors = {
  white_1: '#8c8c8c',
  white_2: '#8c8c8c',
  white_3: '#8c8c8c',
  white_4: '#8c8c8c',
  white_5: '#8c8c8c',
  bronze_1: '#cd7f32',
  bronze_2: '#cd7f32',
  bronze_3: '#cd7f32',
  silver_1: '#a0a0a0',
  silver_2: '#a0a0a0',
  silver_3: '#a0a0a0',
  gold_1: '#faad14',
  gold_2: '#faad14',
  gold_3: '#faad14',
  platinum_1: '#722ed1',
  platinum_2: '#722ed1',
  platinum_3: '#722ed1',
  diamond_1: '#1890ff',
  diamond_2: '#1890ff',
  diamond_3: '#1890ff',
  legend: '#eb2f96',
};

const UserList = () => {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetailVisible, setUserDetailVisible] = useState(false);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [planFilter, setPlanFilter] = useState(null);
  const [trialFilter, setTrialFilter] = useState(null);
  const [sortInfo, setSortInfo] = useState({ field: 'registered_at', order: 'descend' });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(
    async (page = 1, pageSize = 20, search = '', filter = null, sortField = 'registered_at', sortOrder = 'descend', trialFilterValue = null) => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        const params = {
          page,
          page_size: pageSize,
        };

        if (search) {
          params.search = search;
        }

        if (filter && filter !== 'all') {
          params.plan_filter = filter;
        }

        if (trialFilterValue) {
          params.trial_filter = trialFilterValue;
        }

        // ソートパラメータを追加
        if (sortField) {
          params.sort_by = sortField;
          params.sort_order = sortOrder || 'descend';
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          },
        );

        if (!response.data || !response.data.users) {
          console.error('Invalid response format:', response.data);
          message.error('サーバーからの応答形式が不正です');
          setData([]);
          return;
        }

        setData(response.data.users);
        setPagination(prev => ({
          ...prev,
          current: response.data.page,
          total: response.data.total,
          pageSize: response.data.page_size,
        }));
      } catch (error) {
        console.error('Failed to fetch users:', error);
        message.error('ユーザー情報の取得に失敗しました');
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchUsers(1, pagination.pageSize, searchText, planFilter, sortInfo.field, sortInfo.order, trialFilter);
  }, []);

  // 検索実行（デバウンス）
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1, pagination.pageSize, searchText, planFilter, sortInfo.field, sortInfo.order, trialFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, planFilter, trialFilter]);

  const handleTableChange = (newPagination, filters, sorter) => {
    // ソート情報を更新
    const newSortField = sorter.field || sorter.columnKey || 'registered_at';
    const newSortOrder = sorter.order || 'descend';
    
    setSortInfo({ field: newSortField, order: newSortOrder });
    
    fetchUsers(
      newPagination.current,
      newPagination.pageSize,
      searchText,
      planFilter,
      newSortField,
      newSortOrder,
      trialFilter,
    );
  };

  const showUserDetail = userId => {
    setSelectedUserId(userId);
    setUserDetailVisible(true);
  };

  const handleUserDetailCancel = () => {
    setSelectedUserId(null);
    setUserDetailVisible(false);
  };

  const handleUserUpdate = () => {
    fetchUsers(pagination.current, pagination.pageSize, searchText, planFilter, sortInfo.field, sortInfo.order, trialFilter);
  };

  const planTags = {
    free: { color: 'default', text: 'フリープラン' },
    lite: { color: 'blue', text: 'ライトプラン' },
    standard: { color: 'success', text: 'スタンダードプラン' },
    standard_new: { color: 'success', text: 'スタンダードプラン' },
    pro: { color: 'blue', text: 'プロプラン' },
    premium: { color: 'warning', text: 'プレミアムプラン' },
    premium_new: { color: 'warning', text: 'プレミアムプラン' },
    custom: { color: 'purple', text: 'カスタムプラン' },
  };

  const columns = [
    {
      title: '会社名/部署名',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 180,
      sorter: true,
      sortOrder: sortInfo.field === 'company_name' ? sortInfo.order : null,
      render: (_, record) => (
        <>
          <div>{record.company_name || '-'}</div>
          {record.department_name && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.department_name}
            </div>
          )}
        </>
      ),
    },
    {
      title: '氏名',
      key: 'name',
      width: 140,
      render: (_, record) => (
        <>
          <div>
            {record.last_name} {record.first_name}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.last_name_kana} {record.first_name_kana}
          </div>
        </>
      ),
    },
    {
      title: 'プラン',
      key: 'plan',
      width: 150,
      render: (_, record) => {
        const plan = record.plan;
        const planInfo = plan
          ? planTags[plan.name] || planTags[plan.type] || planTags.custom
          : planTags.free;
        return (
          <>
            <Badge dot={record.has_pending_custom_plan || record.is_trialing} color={record.is_trialing ? 'red' : undefined}>
              <Tag color={planInfo.color}>
                {plan?.display_name || planInfo.text}
              </Tag>
            </Badge>
            {record.is_canceling && (
              <div
                style={{ fontSize: '11px', color: '#ff4d4f', marginTop: '4px' }}
              >
                自動キャンセル
              </div>
            )}
            {record.is_trialing && (
              <div style={{ fontSize: '11px', color: '#722ed1', marginTop: '4px' }}>
                トライアル中
              </div>
            )}
          </>
        );
      },
    },
    {
      title: 'ランク',
      key: 'rank',
      width: 120,
      render: (_, record) => {
        if (!record.current_rank) {
          return <Text type="secondary">-</Text>;
        }
        const displayName =
          rankDisplayNames[record.current_rank] || record.current_rank;
        const color = rankColors[record.current_rank] || '#8c8c8c';
        return (
          <Tag
            icon={<TrophyOutlined />}
            style={{ borderColor: color, color: color }}
          >
            {displayName}
          </Tag>
        );
      },
    },
    {
      title: 'クレジット',
      key: 'credits',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const used = record.credit_used || 0;
        const limit = record.credit_limit || 0;
        const bonus = record.bonus_credit_balance || 0;
        const isCustom = record.plan?.type === 'custom';
        return (
          <div>
            <div>
              <Text>{used} / {limit}</Text>
            </div>
            <div style={{ fontSize: '11px', color: '#888' }}>
              {isCustom ? '期間内上限' : '月間上限'}
            </div>
            {bonus > 0 && (
              <div style={{ fontSize: '11px', color: '#722ed1' }}>
                +{bonus} ボーナス
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '次回プラン更新',
      key: 'next_renewal_date',
      width: 110,
      render: (_, record) => {
        if (!record.subscription?.next_renewal_date) return '-';
        const date = moment(record.subscription.next_renewal_date)
          .tz('Asia/Tokyo')
          .format('YYYY/MM/DD');
        if (record.subscription.cancel_at_period_end) {
          return <Text type="danger">{date}</Text>;
        }
        return date;
      },
    },
    {
      title: '最終ログイン',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 140,
      sorter: true,
      sortOrder: sortInfo.field === 'last_login_at' ? sortInfo.order : null,
      render: (_, record) => {
        if (!record.last_login_at) return '-';
        return dayjs(record.last_login_at).format('YYYY/MM/DD HH:mm');
      },
    },
  ];

  // 承認待ちフィルタ
  const displayData = showPendingOnly
    ? data.filter(user => user.has_pending_custom_plan)
    : data;

  return (
    <>
      <Helmet>
        <title>ユーザー管理 | magicss</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: '管理者機能' }, { title: 'ユーザー管理' }]}
      />
      <Title level={1} className="gradient-text" style={{ fontSize: '24px' }}>
        ユーザー管理
      </Title>
      <Divider style={{ margin: '24px 0' }} />

      <Space
        style={{
          marginBottom: 16,
          width: '100%',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space>
          <Checkbox
            checked={showPendingOnly}
            onChange={e => setShowPendingOnly(e.target.checked)}
          >
            カスタムプラン承認待ちのみ
          </Checkbox>
          <Select
            placeholder="プランで絞り込み"
            allowClear
            style={{ width: 180 }}
            value={planFilter}
            onChange={value => setPlanFilter(value)}
            options={[
              { value: 'free', label: 'フリープラン' },
              { value: 'lite', label: 'ライトプラン' },
              { value: 'standard', label: 'スタンダードプラン' },
              { value: 'custom', label: 'カスタムプラン' },
              { value: 'canceling', label: 'キャンセル予約中' },
            ]}
          />
          <Select
            placeholder="トライアル"
            allowClear
            style={{ width: 140 }}
            value={trialFilter}
            onChange={value => setTrialFilter(value)}
            options={[
              { value: 'trialing', label: 'トライアル中' },
              { value: 'trial_canceling', label: 'キャンセル予約中' },
              { value: 'trial_converted', label: '転換済み' },
              { value: 'trial_canceled', label: 'キャンセル済み' },
            ]}
          />
        </Space>
        <Input.Search
          placeholder="メールアドレス、会社名、氏名で検索"
          allowClear
          enterButton={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={displayData}
        rowKey="user_id"
        onRow={record => ({
          onClick: () => showUserDetail(record.user_id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}件`,
        }}
        onChange={handleTableChange}
        loading={loading}
        size="middle"
      />

      <UserDetail
        visible={userDetailVisible}
        onCancel={handleUserDetailCancel}
        userId={selectedUserId}
        onUpdate={handleUserUpdate}
      />
    </>
  );
};

export default UserList;
